import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Shield, KeyRound, FileSearch, AlertCircle, Fingerprint, ShieldCheck, Lock, CheckCircle } from "lucide-react";

const TEAL = "#00A8B3";
const GREEN = "#28A745";
const NAVY = "#1A1F36";
const MID = "#6B7280";

const bullets = [
  { icon: KeyRound, text: "OTP-gated consent sharing. Patients decide who sees what", color: TEAL },
  { icon: FileSearch, text: "Traceable, auditable access logs for every record view", color: GREEN },
  { icon: Shield, text: "Non-diagnostic AI. Summarizes, never diagnoses", color: TEAL },
  { icon: Fingerprint, text: "Admin-safe architecture. No bulk data downloads", color: "#0097A7" },
  { icon: AlertCircle, text: "Emergency readiness. Break-glass protocols for critical care", color: "#FF6B6B" },
];

const badges = [
  { label: "HIPAA Ready", bg: `${TEAL}15`, color: TEAL },
  { label: "DPDP Compliant", bg: `${GREEN}15`, color: GREEN },
  { label: "ABDM Native", bg: "#0097A715", color: "#0097A7" },
];

export const SafeSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="safety" className="py-20 lg:py-28 section-bold relative overflow-hidden" ref={ref}>
      {/* Floating background decorations */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <motion.div animate={{ y: [0, -15, 0], rotate: [0, 8, 0] }} transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-[10%] right-[8%]">
          <ShieldCheck className="w-48 h-48" style={{ color: 'rgba(255,255,255,0.03)' }} />
        </motion.div>
        <motion.div animate={{ y: [0, 12, 0], rotate: [0, -5, 0] }} transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-[8%] left-[5%]">
          <Lock className="w-32 h-32" style={{ color: 'rgba(255,255,255,0.02)' }} />
        </motion.div>
        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.03, 0.06, 0.03] }} transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-[40%] left-[20%] w-64 h-64 rounded-full" style={{ background: `${TEAL}10` }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} className="text-center mb-12">
          <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}}
            className="text-spaced mb-3 block" style={{ color: TEAL }}>S A F E T Y</motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">Safe & Reliable AI</h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Consent-driven, traceable, non-diagnostic, built for DPDP compliance from day one.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Main card with animated border */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3 }}>
            <div className="relative rounded-2xl p-[2px] overflow-hidden" style={{
              background: `linear-gradient(135deg, ${TEAL}40, transparent 40%, transparent 60%, ${GREEN}40)`,
              backgroundSize: '200% 200%',
              animation: 'borderShift 6s ease infinite',
            }}>
              <div className="rounded-2xl p-8 lg:p-10" style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(16px)' }}>
                {/* Grid of safety features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {bullets.map((b, i) => (
                    <motion.div key={i}
                      initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: i * 0.12 + 0.4 }}
                      whileHover={{ scale: 1.02, x: 4 }}
                      className="flex items-start gap-3 p-4 rounded-xl transition-all duration-300"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = `${b.color}12`; e.currentTarget.style.borderColor = `${b.color}30`; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.15, rotate: 8 }}
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${b.color}20` }}>
                        <b.icon className="w-5 h-5" style={{ color: b.color }} />
                      </motion.div>
                      <div>
                        <p className="text-sm font-medium text-white leading-relaxed">{b.text}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Compliance badges */}
                <motion.div initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 1.2 }}
                  className="flex flex-wrap justify-center gap-3 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  {badges.map((b, i) => (
                    <motion.div key={b.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ delay: i * 0.1 + 1.3, type: "spring" }}
                      whileHover={{ scale: 1.08 }}
                      className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold"
                      style={{ background: b.bg, color: b.color }}>
                      <CheckCircle className="w-3.5 h-3.5" />
                      {b.label}
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes borderShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </section>
  );
};
